<template>
	<AuthLayout redirectTo="/devices">
		<ColumnContainer>
			<Column title="Zones">
				<Zones @selected="onSelectedZone" :selectKeyboardListeners="selectKeyboardListeners"></Zones>
			</Column>
			<Column title="Devices">
				<ListView class="select-device-list" @selected="onSelectedDevice" v-if="filteredDevices" :items="filteredDevices"
				          :showIndex="true" :selectKeyboardListeners="selectKeyboardListeners"></ListView>
			</Column>
			<Column title="Device Information">
				<DeviceInformation @selected="onSelectedCapability" :device="selectedDevice"
				                   v-if="filteredDevices && selectedDevice"></DeviceInformation>
			</Column>
		</ColumnContainer>
        <Dialog v-if="selectedCapability" :title="selectedCapability.capabilityName" >
			<div v-if="selectedCapability.setable" class="Center">
                <div v-if="selectedCapability.type === 'boolean'">
                    <div v-if="!selectedCapability.getable">
                        <br/>
			            <Button @onPress="setCapabilityTrue" :title="selectedCapability.capabilityName"/>
			        </div>
			        <div v-else>
                        <ListView @selected="onCapabilityValueChange" :items="booleanItems"
				          :showIndex="true" :selectKeyboardListeners="selectKeyboardListeners"></ListView>
			        </div>
                </div>
                <div v-if="selectedCapability.type === 'number'">
                    <Slider 
                        :min="selectedCapability.min"
                        :max="selectedCapability.max"
                        :step="selectedCapability.step||0.1"
                        :initialValue="selectedCapability.value" 
                        @onValue="onNumberCapabilityValueChange"/>
                </div>
                <div v-if="selectedCapability.type === 'enum'">
                    <ListView @selected="onCapabilityValueChange" :items="enumItems"
				          :showIndex="true" :selectKeyboardListeners="selectKeyboardListeners"></ListView>
                </div>
			</div>
			<div class="Center" v-else>
    			{{selectedCapabilityValue}}
			</div>
		</Dialog>
	</AuthLayout>
</template>

<script>
	import { Component, Vue } from 'vue-property-decorator';
	import { Getter, Action } from 'vuex-class';
	import AuthLayout from '../../layouts/Auth/index.vue';
	import Dialog from '../../components/Dialog';
	import ListView from '../../components/ListView';
	import ColumnContainer from '../../components/ColumnContainer';
	import Column from '../../components/Column';
	import Zones from '../../components/ZoneSelector';
	import KeyListenerView from '../../components/KeyListenerView';
	import DeviceInformation from '../../components/DeviceInformation';
	
	import Button from '../../components/Button';
	import Slider from '../../components/Slider';
	

	@Component({
		components: {
			AuthLayout,
			Dialog,
			ListView,
			ColumnContainer,
			Column,
			Zones,
			DeviceInformation,
			
			Button,
			Slider,
		},
	})
	export default class ViewDevices extends KeyListenerView {
		@Getter('api/homey/devices/all') devices;
		@Action('api/homey/devices/retrieveAll') getDevices;
		@Action('api/homey/devices/setCapabilityValue') setCapabilityValue;
		selectedDevice = null;
		selectedZoneId = null;
		selectedCapability = null;
		
		

		async mounted() {
			this.getDevices();
			this.addKeyboardListener('LEFT', this.onBackKey, 'Back');
			this.addKeyboardListener('ESC', this.onBackKey);
			this.addKeyboardListener('BACKSPACE', this.onBackKey);
			super.mounted();
		}

		destroyed() {
			super.destroyed();
		}
		
		_throttle(func, wait, options) {
            var context, args, result;
            var timeout = null;
            var previous = 0;
            var later = function() {
                previous = Date.now();
                timeout = null;
                result = func.apply(context, args);
                if (!timeout) context = args = null;
            };
            return function() {
                var now = Date.now();
                var remaining = wait - (now - previous);
                context = this;
                args = arguments;
                if (remaining <= 0 || remaining > wait) {
                    if (timeout) {
                        clearTimeout(timeout);
                        timeout = null;
                    }
                    previous = now;
                    result = func.apply(context, args);
                    if (!timeout) context = args = null;
                } else if (!timeout) {
                    timeout = setTimeout(later, remaining);
                }
                return result;
            };
        };
		
		unselectDevice() {
    		if(!this.selectedDevice) return;
    		this.selectedDevice = null;
		}

		onBackKey() {
			if (this.selectedCapability) return this.selectedCapability = null;
			if (this.selectedDevice) return this.unselectDevice();
			this.selectedZoneId = null;
		}

		onSelectedZone(zone) {
			this.selectedZoneId = zone.id;
		}

		onSelectedDevice({ device }) {
			if (!device.available) return;
			this.unselectDevice();
			this.selectedDevice = device;
		}

		onSelectedCapability(capability) {
			this.selectedCapability = capability;
			this._setCapabilityValueThrottled = this._throttle(this.setCapabilityValue, 1000);
		}

		get selectKeyboardListeners() {
			return [
				['ENTER', 'Select'],
				['RIGHT']
			]
		}

		get filteredDevices() {
			if (!this.selectedZoneId || !this.devices) return null;
			const devices = this.devices.filter(device => device.zone === this.selectedZoneId);
			if (this.devices.length === 0) {
				// TODO: write message that you need to buy devices
			}
			return devices.map(device => ({
				title: device.available ? device.name : `${device.name} (Unavailable)`,
				class: { offline: !device.available },
				device
			}))
		}
		
		get selectedCapabilityValue() {
    		return this.selectedCapability.formattedValue;
		}
		
		_setCapabilityValueThrottled(...args) {
    		return this.setCapabilityValue(...args);
		}
		
		onCapabilityValueChange(item, keepOpen = false) {
    		this._setCapabilityValueThrottled({deviceId: this.selectedDevice.id, capability: this.selectedCapability.id, value: item.value});
            if(!keepOpen) this.selectedCapability = null;
		}
		
		onNumberCapabilityValueChange(item) {
    		item.value = Math.round(item.value*100)/100;
    		this.onCapabilityValueChange(item, true)
		}
		
		setCapabilityTrue() {
    		this.onCapabilityValueChange({value: true}, true);
		}
		
		get booleanItems() {
    		return [{
        		title: 'Yes',
        		value: true,
    		},{
        		title: 'No',
        		value: false
    		}];
		}
		
		get enumItems() {
    		return this.selectedCapability.values
    		    .map(val => {
        		    return {
            		    title: val.title,
            		    value: val.id
        		    }
                });
		}
	}
</script>

<style lang="scss">
	.select-device-list .offline {
		color: darkgrey;
	}
</style>